/**
 * CandidateStore — CRUD for resume-candidates collection.
 *
 * Each candidate doc:
 * {
 *   projectId, fileName, storagePath,
 *   name, email, phone, education, careerSummary, skillSummary,
 *   extractionStatus: 'pending'|'processing'|'complete'|'error',
 *   extractionError,
 *   scores: { [promptId]: { score, reasoning, status, error } },
 *   disposition: ''|'advance'|'hold'|'reject',
 *   followUp: bool, interview: bool, rejected: bool,
 *   createdAt, updatedAt
 * }
 */
export class CandidateStore {
    constructor(db) {
        this._db = db;
        this._collection = db.collection('resume-candidates');
        this._unsub = null;
        this._candidates = [];
        this._listeners = [];
        this._errorListeners = [];
        this._projectId = null;
    }

    /** Subscribe to candidates for a specific project */
    subscribeProject(projectId) {
        this.dispose();
        this._projectId = projectId;
        this._unsub = this._collection
            .where('projectId', '==', projectId)
            .orderBy('createdAt', 'asc')
            .onSnapshot(snap => {
                this._candidates = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                this._notify();
            }, err => {
                console.error('Candidate subscription error:', err);
                this._errorListeners.forEach(fn => fn(err));
            });
    }

    onChange(fn) { this._listeners.push(fn); }
    onError(fn) { this._errorListeners.push(fn); }

    getAll() { return this._candidates; }

    getById(id) { return this._candidates.find(c => c.id === id) || null; }

    /** Create a candidate entry after uploading a resume PDF */
    async create(projectId, fileName, storagePath) {
        const ref = await this._collection.add({
            projectId,
            fileName,
            storagePath,
            name: '',
            email: '',
            phone: '',
            education: '',
            careerSummary: '',
            skillSummary: '',
            extractionStatus: 'pending',
            extractionError: null,
            scores: {},
            responseStatus: 'no_response',
            disposition: '',
            followUp: false,
            interview: false,
            rejected: false,
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
        return ref.id;
    }

    /** Update candidate fields (disposition, flags, etc.) */
    async update(id, fields) {
        await this._collection.doc(id).update({
            ...fields,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
    }

    async remove(id) {
        await this._collection.doc(id).delete();
    }

    dispose() {
        if (this._unsub) this._unsub();
        this._candidates = [];
    }

    _notify() {
        for (const fn of this._listeners) fn(this._candidates);
    }
}
